import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Loader2,
  Lock,
  Key,
  Shield,
  CheckCircle2,
  ArrowLeft,
  Eye,
  EyeOff,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { authAPI } from "@/services/api";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";

export function ResetPasswordForm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();
  const { toast } = useToast();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      setIsTokenValid(true);
    } else {
      setIsTokenValid(false);
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      toast({
        title: "Error",
        description: "Token no válido",
        variant: "destructive",
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Las contraseñas no coinciden",
        variant: "destructive",
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "La contraseña debe tener al menos 6 caracteres",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await authAPI.resetPassword({
        token,
        new_password: newPassword,
        email: email || undefined,
      });

      setSuccess(true);
      toast({
        title: "¡Éxito!",
        description: "Tu contraseña ha sido actualizada correctamente",
        variant: "default",
      });

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Ocurrió un error al restablecer la contraseña");
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (isTokenValid === null) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4">
        <AnimatedBackground />
        <div className="relative z-10 text-center bg-white/20 backdrop-blur-xl p-8 rounded-2xl border border-white/30 shadow-2xl">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-700 font-medium">Validando enlace de recuperación...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-hidden relative">
      <AnimatedBackground />

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-8">
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header Area */}
          <div className="text-center mb-8 space-y-4">
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-tr from-blue-600 to-purple-600 rounded-2xl shadow-xl shadow-blue-500/30 ring-4 ring-white/50"
            >
              <Key className="h-8 w-8 text-white" />
            </motion.div>
            <div className="space-y-2">
              <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Nueva Contraseña
              </h1>
              <p className="text-slate-500 font-medium">
                Crea una contraseña segura para tu cuenta
              </p>
            </div>
          </div>

          {!isTokenValid ? (
            <Card className="bg-white/70 backdrop-blur-md border-white/50 shadow-2xl rounded-3xl overflow-hidden border">
              <CardContent className="p-8 text-center space-y-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <AlertCircle className="h-8 w-8 text-red-600" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold text-slate-800">Enlace No Válido</h3>
                  <p className="text-slate-600">
                    Este enlace de recuperación ha expirado o ya fue utilizado.
                  </p>
                </div>
                <Button
                  onClick={handleBackToLogin}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 rounded-xl transition-all duration-300"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Volver al Login
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {success ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-white/80 backdrop-blur-md p-8 rounded-3xl border border-green-100 shadow-2xl text-center space-y-6"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <CheckCircle2 className="h-10 w-10 text-green-600" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-bold text-slate-800">¡Todo listo!</h3>
                    <p className="text-slate-600">Tu contraseña ha sido actualizada correctamente.</p>
                  </div>
                  <p className="text-sm text-slate-400 animate-pulse">Redirigiendo al login...</p>
                </motion.div>
              ) : (
                <Card className="bg-white/70 backdrop-blur-md border-white/50 shadow-2xl rounded-3xl overflow-hidden border">
                  <CardContent className="p-8">
                    {error && (
                      <Alert variant="destructive" className="mb-6 bg-red-50/50 border-red-200">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-4">
                        <div className="space-y-2 group">
                          <Label className="text-sm font-semibold text-slate-700 ml-1">
                            Nueva Contraseña
                          </Label>
                          <div className="relative">
                            <div className="absolute inset-0 bg-blue-500/5 blur-lg group-focus-within:bg-blue-500/10 transition-all rounded-xl" />
                            <div className="relative">
                              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                              <Input
                                type={showPassword ? "text" : "password"}
                                placeholder="Escribe tu nueva contraseña"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="pl-11 pr-11 h-12 bg-white/50 border-slate-200 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 rounded-xl transition-all"
                                required
                                disabled={isLoading}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                              >
                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2 group">
                          <Label className="text-sm font-semibold text-slate-700 ml-1">
                            Confirmar Contraseña
                          </Label>
                          <div className="relative">
                            <div className="absolute inset-0 bg-blue-500/5 blur-lg group-focus-within:bg-blue-500/10 transition-all rounded-xl" />
                            <div className="relative">
                              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                              <Input
                                type={showConfirmPassword ? "text" : "password"}
                                placeholder="Repite tu nueva contraseña"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="pl-11 pr-11 h-12 bg-white/50 border-slate-200 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 rounded-xl transition-all"
                                required
                                disabled={isLoading}
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                              >
                                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100/50 space-y-1">
                        <div className="flex items-center gap-2 text-blue-800">
                          <Shield className="h-4 w-4" />
                          <span className="text-xs font-bold uppercase tracking-wider">Seguridad</span>
                        </div>
                        <p className="text-xs text-blue-600/80 leading-relaxed">
                          Usa al menos 6 caracteres y combina letras con números para mayor protección.
                        </p>
                      </div>

                      <Button
                        type="submit"
                        disabled={isLoading || !newPassword || !confirmPassword}
                        className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:hover:translate-y-0"
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                            Actualizando...
                          </>
                        ) : (
                          <>
                            <Key className="h-5 w-5 mr-2" />
                            Actualizar Contraseña
                          </>
                        )}
                      </Button>

                      <Button
                        type="button"
                        variant="ghost"
                        onClick={handleBackToLogin}
                        className="w-full text-slate-500 hover:text-slate-800 hover:bg-slate-100/50"
                        disabled={isLoading}
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Volver al inicio
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-xs text-slate-400 flex items-center justify-center gap-1">
              <Shield className="h-3 w-3" />
              Conexión segura protegida con cifrado de extremo a extremo
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
